import { createVisualRandom, toVisualSeed } from './seeded-random'

type Vec2 = [number, number]

interface AuroraFieldOptions {
  seed: string
  intensity: number
  scale: number
}

interface AuroraPebble {
  center: Vec2
  radius: Vec2
  rotation: number
  strength: number
}

interface AuroraCurve {
  start: Vec2
  control: Vec2
  end: Vec2
  width: number
  strength: number
}

interface AuroraField {
  seed: number
  warp: number
  pebbles: AuroraPebble[]
  curves: AuroraCurve[]
}

interface CurveContext {
  random: () => number
  intensity: number
  scale: number
}

const PEBBLE_COUNT = 5
const CURVE_COUNT = 2

function buildPebble(
  index: number,
  random: () => number,
  scale: number,
): AuroraPebble {
  const isAnchor = index === 0
  return {
    center: isAnchor
      ? [0.46 + random() * 0.16, 0.36 + random() * 0.26]
      : [-0.08 + random() * 1.16, -0.06 + random() * 1.12],
    radius: isAnchor
      ? [(0.3 + random() * 0.18) * scale, (0.26 + random() * 0.2) * scale]
      : [(0.2 + random() * 0.2) * scale, (0.18 + random() * 0.24) * scale],
    rotation: random() * Math.PI,
    strength: isAnchor ? 0.38 + random() * 0.12 : 0.08 + random() * 0.12,
  }
}

function buildCurve(index: number, context: CurveContext): AuroraCurve {
  const { random, intensity, scale } = context
  const angle = -0.45 + random() * 0.9
  const direction: Vec2 = [Math.cos(angle), Math.sin(angle)]
  const normal: Vec2 = [-direction[1], direction[0]]
  const center: Vec2 =
    index === 0
      ? [-0.02 + random() * 0.2, 0.54 + random() * 0.3]
      : [0.84 + random() * 0.24, 0.24 + random() * 0.5]
  const length = 0.86 + random() * 0.34
  const bend = (random() - 0.5) * 0.2
  return {
    start: [
      center[0] - direction[0] * length,
      center[1] - direction[1] * length,
    ],
    control: [center[0] + normal[0] * bend, center[1] + normal[1] * bend],
    end: [center[0] + direction[0] * length, center[1] + direction[1] * length],
    width: (0.28 + random() * 0.18) * scale,
    strength: (0.26 + random() * 0.18) * intensity,
  }
}

function buildAuroraField(options: AuroraFieldOptions): AuroraField {
  const seed = toVisualSeed(options.seed)
  const random = createVisualRandom(seed)
  const curveContext = {
    random,
    intensity: options.intensity,
    scale: options.scale,
  }
  return {
    seed,
    warp: 0.018 + random() * 0.018,
    pebbles: Array.from({ length: PEBBLE_COUNT }, (_, index) =>
      buildPebble(index, random, options.scale),
    ),
    curves: Array.from({ length: CURVE_COUNT }, (_, index) =>
      buildCurve(index, curveContext),
    ),
  }
}

export { buildAuroraField, CURVE_COUNT, PEBBLE_COUNT }
export type { AuroraCurve, AuroraField, AuroraPebble }
